/**
 * Build an image-to-image render prompt from the current project items.
 * Used by the "AI Render" button to ask Gemini Vertex for a photorealistic
 * version of the three.js canvas snapshot.
 *
 * The prompt names the table collection, size, base, top finish, plus
 * the chair collection, frame, upholstery and qty — so the model preserves
 * material choice instead of inventing arbitrary finishes.
 */

import {
  COLLECTION_BY_CODE, BASE_BY_CODE,
  CERAMIC_TOPS, WOOD_TOPS, GLASS_TOPS, CRYSTALART_TOPS,
  CHAIR_COLLECTIONS, CHAIR_FRAMES,
  CHAIR_FABRICS, CHAIR_MICRO_NUBUCK, CHAIR_SOFT_LEATHER,
  CHAIR_SOFT_LEATHER_GLOVE, CHAIR_SYNTHETIC_LEATHER,
} from '../data/catalog.js';

const ALL_TOPS = [...GLASS_TOPS, ...CRYSTALART_TOPS, ...CERAMIC_TOPS, ...WOOD_TOPS];

function topName(code) {
  return ALL_TOPS.find(t => t.code === code)?.name || code;
}

function baseName(code) {
  const b = BASE_BY_CODE[code];
  return b ? `${b.name} ${b.code}` : code;
}

function chairFrameName(collection, code) {
  const frame = (CHAIR_FRAMES[collection] || []).find(f => f.code === code);
  return frame?.name || code;
}

const ALL_UPHOL = [
  ...CHAIR_FABRICS,
  ...CHAIR_MICRO_NUBUCK,
  ...CHAIR_SOFT_LEATHER,
  ...CHAIR_SOFT_LEATHER_GLOVE,
  ...CHAIR_SYNTHETIC_LEATHER,
];

function upholsteryName(code) {
  if (code === 'COM') return 'customer-supplied fabric';
  if (code === 'COL') return 'customer-supplied leather';
  return ALL_UPHOL.find(u => u.code === code)?.name || code;
}

function describeTable(item) {
  const col = COLLECTION_BY_CODE[item.collection]?.name || item.collection;
  const parts = [
    `${col} ${item.sizeKey} table`,
    `top: ${topName(item.topCode)}`,
    `base: ${baseName(item.baseCode)}`,
  ];
  if (item.insertOrProfile) parts.push(`insert/profile: ${item.insertOrProfile}`);
  if (item.qty > 1) parts.push(`qty ${item.qty}`);
  return parts.join(', ');
}

function describeChair(item) {
  const col = CHAIR_COLLECTIONS.find(c => c.code === item.collection)?.name || item.collection;
  const arms = item.sizeKey === 'B' ? 'with armrests' : 'no armrests';
  const frame = chairFrameName(item.collection, item.frameCode);
  const uphol = upholsteryName(item.upholsteryCode);
  return `${item.qty}× ${col} chair (${arms}), frame: ${frame}, upholstery: ${uphol}`;
}

export function buildRenderPrompt(items) {
  if (!items || items.length === 0) {
    return 'Photorealistic 4K dining-room render of this scene. Studio lighting, soft shadows, neutral background. Preserve the exact geometry, proportions and material finishes shown.';
  }

  const tables = items.filter(it => it.kind === 'table');
  const chairs = items.filter(it => it.kind === 'chair');

  const tableLines = tables.map(t => `  - ${describeTable(t)}`).join('\n');
  const chairLines = chairs.map(c => `  - ${describeChair(c)}`).join('\n');

  const sections = [];
  if (tables.length) sections.push(`Tables:\n${tableLines}`);
  if (chairs.length) sections.push(`Chairs:\n${chairLines}`);

  return [
    'Render this scene as a photorealistic 4K studio photograph of a Cattelan Italia dining set.',
    'Preserve the exact geometry, table top shape, base form, chair count and positions visible in the input image.',
    'Match these material specifications exactly:',
    '',
    sections.join('\n\n'),
    '',
    'Style: clean studio lighting from upper-left, soft contact shadows, neutral light beige background (#F3EEE4), no extra furniture, no plants, no people. Cinematic depth of field with the table top in sharp focus.',
  ].join('\n');
}
